import React, { useState, useEffect } from 'react';
import Modal from "./Modal";
import ThemableSvgIcon from "./theme/ThemableSvgIcon";
import Price from "./Price";
import {getFromHub, postToHub} from "../actions/networkActions";
import {convertDMYtoYMD, formatPortDate, formatTimeHM} from "../utils/dateTimeUtils";
import {checkForOverlaps} from "../utils/overlapUtils";
import OverlapWarning from "./OverlapWarning";
import {updateCart} from "../actions/cartActions";
import Checkbox from "./Checkbox";
import TextInputWithLabel from "./TextInputWithLabel";
import ModalFooter from "./ModalFooter";
import {removeTacEntryFromCart} from "../actions/tacActions";
import {themableImagePath} from "../utils/imageUtils";
import SelectDropdown from "./SelectDropdown";
import { getChildAgeRange } from '../utils/cartUtils';
import { Trans, useLingui } from '@lingui/react/macro';
import { i18n } from '@lingui/core';
import { getTranslation } from '../utils/translationUtils';

const otherPrefix = "Other: ";

const RestaurantAvailabilityModal = (props) => {
    const { preselectedDetails, restaurant, date, isActivity, closeModal, onError, onSuccess } = props;

    const { t } = useLingui();

    const dietaryOptions = {
        vegetarian: { value: "Vegetarian", label: t({id: "dietary_vegetarian"}) },
        diabetic: { value: "Diabetic Desserts", label: t({id: "dietary_diabetic_desserts"}) },
        gluten: { value: "Gluten Free", label: t({id: "dietary_gluten"}) },
        coeliac: { value: "Coeliac", label: t({id: "dietary_coeliac"}) },
        lowSalt: { value: "Low Salt", label: t({id: "dietary_low_salt"}) },
        vegan: { value: "Vegan", label: t({id: "dietary_vegan"}) },
        lactose: { value: "Lactose Intolerant", label: t({id: "dietary_lactose"}) },
        other: { value: "Other", label: t({id: "dietary_other"}) },
    };

    const initialNrAdults = preselectedDetails ?
        preselectedDetails.nrAdults :
        (restaurant.minimumNumberOfAdults < 2 && restaurant.maximumNumberOfAdults > 1 ? 2 : restaurant.minimumNumberOfAdults);
    const initialNrChildren = preselectedDetails ? preselectedDetails.nrChildren : 0;

    const initialSpecialRequirements = {};
    let initialOtherAllergyText = "";
    Object.keys(dietaryOptions).forEach(key => {
        initialSpecialRequirements[key] = preselectedDetails && preselectedDetails.specialRequirements.includes(dietaryOptions[key].value);
    });
    const otherRequirement = preselectedDetails && preselectedDetails.specialRequirements.find(requirement => requirement.startsWith(otherPrefix));
    if (otherRequirement) {
        initialSpecialRequirements.other = true;
        initialOtherAllergyText = otherRequirement.substring(otherPrefix.length);
    }

    const [groupSizeTooLarge, setGroupSizeTooLarge] = useState(false);
    const [nrAdultsSelected, setNrAdultsSelected] = useState(initialNrAdults);
    const [nrChildrenSelected, setNrChildrenSelected] = useState(initialNrChildren);
    const [isLoading, setIsLoading] = useState(false);
    const [availabilities, setAvailabilities] = useState([]);
    const [selectedAvailability, setSelectedAvailability] = useState(false);
    const [isDietaryFormOpen, setIsDietaryFormOpen] = useState(false);
    const [isOtherAllergyInputShown, setIsOtherAllergyInputShown] = useState(!!otherRequirement);
    const [otherAllergyText, setOtherAllergyText] = useState(initialOtherAllergyText);
    const [otherAllergyTextError, setOtherAllergyTextError] = useState("");
    const [specialRequirements, setSpecialRequirements] = useState(initialSpecialRequirements);
    const [overlapElements, setOverlapElements] = useState([]);
    const [addingToCart, setAddingToCart] = useState(false);

    const loadAvailabilities = (preselect = false, adults, children) => {
        setIsLoading(true);
        getFromHub(
            `prebooking/tac/templates/${restaurant.templateId}/serviceProfiles/${restaurant.id}/availabilities?date=${convertDMYtoYMD(date)}&numberOfAdults=${adults}&numberOfChildren=${children}`,
            (response) => updateAvailabilities(response, preselect)
        );
    };

    useEffect(() => {
        loadAvailabilities(true, nrAdultsSelected, nrChildrenSelected);
    }, []);

    const updateAvailabilities = (response, preselect = false) => {
        const preselectedDateTime = preselectedDetails ? `${date} ${preselectedDetails.time}` : null;
        let newSelectedAvailability = false;
        // TODO - sorting should be done in the hub
        const newAvailabilities = response.availabilities
            .filter(availability => availability.numberOfAvailabilities > 0)
            .sort((a,b) =>  a.fromTime > b.fromTime ? 1 : -1)
            .map((availability, index) => {
                const isSelected = preselect && availability.fromTime === preselectedDateTime;
                if(isSelected) {
                    newSelectedAvailability = index;
                }

                return {...availability, selected: isSelected}
            });

        setIsLoading(false);
        setSelectedAvailability(newSelectedAvailability);
        setAvailabilities(newAvailabilities);
    };

    const validateDropdownChange = (adults, children) => {
        if ((adults + children) > restaurant.maximumNumberOfPersons) {
            setGroupSizeTooLarge(true);
            setAvailabilities([]);
            setSelectedAvailability(false);
        } else {
            setGroupSizeTooLarge(false);
            loadAvailabilities(false, adults, children);
        }
    };

    const handleDropdownChange = (e) => {
        const value = parseInt(e.target.value);
        const isAdult = e.target.id === "adults-dropdown";

        let newAdults = isAdult ? value : nrAdultsSelected;
        let newChildren = isAdult ? nrChildrenSelected : value;

        if (isAdult) {
            setNrAdultsSelected(value);
        } else {
            setNrChildrenSelected(value);
        }

        validateDropdownChange(newAdults, newChildren);
    };

    const handleSlotSelect = (selectedIndex, availability) => {
        if(availability.numberOfAvailabilities === 0) {
            return;
        }

        const newAvailabilities = availabilities.map((av, index) => ({
            ...av,
            selected: index === selectedIndex,
        }));

        setAvailabilities(newAvailabilities);
        setSelectedAvailability(selectedIndex);
    };

    const toggleDietaryForm = () => {
        setIsDietaryFormOpen(!isDietaryFormOpen);
    };

    const handleCheckboxChange = (key) => {
        const newSpecialRequirements = {...specialRequirements};
        newSpecialRequirements[key] = !newSpecialRequirements[key];

        setSpecialRequirements(newSpecialRequirements);
        setIsOtherAllergyInputShown(key === "other" ? !isOtherAllergyInputShown : isOtherAllergyInputShown);
    };

    const handleAllergyTextChange = (value) => {
        if(value.length > 30) {
            setOtherAllergyTextError(t({id: "allergy_text_limit"}));
            return;
        }

        setOtherAllergyText(value);
        setOtherAllergyTextError("");
    };

    const prepareSpecialRequirements = () => {
        const reqs = [];
        Object.keys(dietaryOptions).forEach(key => {
            if(specialRequirements[key]) {
                if(key === "other") {
                    reqs.push(`${otherPrefix}${otherAllergyText}`)
                }
                else {
                    reqs.push(dietaryOptions[key].value);
                }
            }
        });

        return reqs;
    };

    const validateOtherAllergy = () => {
        if(specialRequirements["other"] && otherAllergyText.trim() === "") {
            setOtherAllergyTextError(t({id: "allergy_text_empty"}));
            return false;
        }
        return true;
    }

    const handleCheckForOverlaps = async () => {
        const selectedAvail = availabilities[selectedAvailability];
        return await checkForOverlaps(selectedAvail.fromTime, selectedAvail.toTime, {
            currentRestaurantId: restaurant.id,
            preselectedDetailsId: preselectedDetails?.id,
            selectedAvailFromTime: selectedAvail.fromTime,
            selectedAvailToTime: selectedAvail.toTime
        });
    };

    const prepareAddToCartPayload = () => {
        const reqs = prepareSpecialRequirements();

        return [{
            type: "Tac",
            action: "add",
            availabilityId: availabilities[selectedAvailability].availabilityId,
            serviceProfile: restaurant.id,
            fromTime: availabilities[selectedAvailability].fromTime,
            toTime: availabilities[selectedAvailability].toTime,
            numberOfAdults: nrAdultsSelected,
            numberOfChildren: nrChildrenSelected,
            specialRequirements: reqs,
        }];
    };

    const postToCart = async () => {
        setAddingToCart(true);
        if (preselectedDetails) {
            await removeTacEntryFromCart(preselectedDetails.id);
        }

        const payload = prepareAddToCartPayload();
        const result = await postToHub('prebooking/cart', payload);

        setAddingToCart(false);

        if (result && result.error) {
            if(result.error.response && result.error.response.data === "AlreadyInCart") {
                onError(
                    isActivity
                        ? t({id: "activity_already_in_planner"})
                        : t({id: "restaurant_already_in_planner"})
                );
            } else {
                onError(t({id: "reservation_add_error"}));
            }
        } else {
            onSuccess();
            await updateCart();
        }
    };

    const addToCart = async () => {
        if(!validateOtherAllergy()) {
            return;
        }

        const overlaps = await handleCheckForOverlaps();
        if(overlaps.length) {
            setOverlapElements(overlaps);
            return;
        }

        postToCart();
    };

    const hideOverlapWarning = () => {
        setOverlapElements([]);
    };

    return (
        <Modal
            show={true}
            onClose={closeModal}
            title={isActivity
                ? <Trans id="activity_booking_modal_title">Activity booking</Trans>
                : <Trans id="restaurant_reservation_modal_title">Restaurant reservation</Trans>
            }
        >
            {overlapElements.length === 0 &&
            <>
                <div className="px-4 pt-2 text-primary-dark">
                    <div className="text-lg border-b pb-2">{getTranslation(restaurant.name)?.toUpperCase()}</div>
                    <div className="text-grey-darker text-lg my-4">{formatPortDate(date)}</div>
                    <div className="my-4">
                        {isActivity
                            ? <Trans id="how_many_guests">How many guests?</Trans>
                            : <Trans id="how_many_people">How many people?</Trans>
                        }
                    </div>
                    <div className="my-4 flex items-end">
                        <SelectDropdown
                            classes="mr-4 w-1/2 md:w-40"
                            selectId="adults-dropdown"
                            label={t({id: "adults_13_plus"})}
                            disabled={isLoading}
                            error={groupSizeTooLarge}
                            options={range(restaurant.minimumNumberOfAdults, restaurant.maximumNumberOfAdults)}
                            handleChange={handleDropdownChange}
                            value={nrAdultsSelected}/>

                        <SelectDropdown
                            classes="w-1/2 md:w-40"
                            selectId="children-dropdown"
                            label={i18n._({
                                id: "children_age_range_label",
                                message: "Children ({ageRange} years)",
                                values: { ageRange: getChildAgeRange(false) },
                            })}
                            disabled={isLoading}
                            error={groupSizeTooLarge}
                            options={range(restaurant.minimumNumberOfChildren, restaurant.maximumNumberOfChildren)}
                            handleChange={handleDropdownChange}
                            value={nrChildrenSelected}/>
                    </div>

                    {/* group size too large to book online */}
                    {groupSizeTooLarge &&
                    <div className="border border-red text-red p-4 mt-4 mb-6">
                        {isActivity
                            ? <Trans id="activity_group_too_large">We are unable to take bookings for more
                                than {restaurant.maximumNumberOfPersons} guest{restaurant.maximumNumberOfPersons > 1 ? "s" : ""} online.
                                Please check onboard for availability.</Trans>
                            : <Trans id="restaurant_group_too_large">We are unable to take bookings for more
                                than {restaurant.maximumNumberOfPersons} passenger{restaurant.maximumNumberOfPersons > 1 ? "s" : ""} online.
                                Please check onboard for availability.</Trans>
                        }
                    </div>
                    }

                    {/* loading availabilities */}
                    {!groupSizeTooLarge && isLoading &&
                    <div className="my-4">
                        <Trans id="searching_availabilities">Searching for available time slots...</Trans>
                    </div>
                    }

                    {/* no error and availabilities loaded*/}
                    {!groupSizeTooLarge && !isLoading &&
                    <div>
                        <div className="my-4">
                            {isActivity
                                ? <Trans id="what_time_activity">What time would you like to start?</Trans>
                                : <Trans id="what_time_table">What time would you like your table?</Trans>
                            }
                        </div>

                        {(availabilities.length === 0) &&
                        <div className="bg-grey-lighter px-4 py-3">
                            {isActivity
                                ? <Trans id="no_activity_slots_available">Unfortunately, we don't have any slots available for your group to book at this time. Please enquire about activity options onboard.</Trans>
                                : <Trans id="no_table_slots_available">Unfortunately, we don't have any tables available for your dining party to book at this time. Please enquire about your dining options onboard.</Trans>
                            }
                        </div>
                        }
                        <div className="mt-4 md:flex md:flex-wrap md:-mx-2">
                            {availabilities.map((availability, index) =>
                                <AvailabilityOption
                                    key={index}
                                    availability={availability}
                                    onClick={() => handleSlotSelect(index, availability)}
                                    currencySymbol={availability.currencySymbol}
                                />)}
                        </div>
                    </div>
                    }


                    {/* Dietary restrictions / alergies */}
                    {!isActivity && !isLoading && (availabilities.length > 0) &&
                    <div>
                        {isDietaryFormOpen &&
                        <>
                            <div className="flex flex-wrap text-grey-dark">
                                {Object.keys(dietaryOptions).map(key =>
                                    <div key={key} className="w-1/2 sm:w-1/3 mt-4">
                                        <Checkbox checked={specialRequirements[key]}
                                                  value={dietaryOptions[key].value}
                                                  handleCheckboxChange={() => handleCheckboxChange(key)}>
                                            {dietaryOptions[key].label}
                                        </Checkbox>
                                    </div>
                                )}
                            </div>
                            <div className="flex bg-grey-lighter py-2 px-4 mt-4">
                                <div className="shrink-0 mr-2">
                                    <img alt="" className="w-6 h-6" src={themableImagePath('infoicon_inverted.svg')} />
                                </div>
                                <div className="text-grey-darker">
                                    <Trans id="dietary_info_banner">We will do our best to accommodate dietary requirements, our food is prepared in kitchens that may contain allergens. Please discuss specific dietary requirements with the restaurant manager once onboard.</Trans>
                                </div>
                            </div>
                        </>
                        }
                        {isDietaryFormOpen && isOtherAllergyInputShown &&
                        <div>
                            <TextInputWithLabel
                                classNames="w-full mt-2"
                                labelClassNames="text-primary-dark leading-loose"
                                label={t({id: "allergy_details_label"})}
                                type="text"
                                value={otherAllergyText}
                                _onChange={value => handleAllergyTextChange(value)}
                                placeholder={t({id: "max_30_characters"})}
                                errorMessage={otherAllergyTextError}
                            />
                        </div>
                        }
                        <div className="my-4 text-primary cursor-pointer" onClick={toggleDietaryForm}>
                            <Trans id="add_dietary_request">Add a dietary/allergy request</Trans>
                            <ThemableSvgIcon
                                name={isDietaryFormOpen ? "chevron-up" : "chevron-down"}
                                className="w-6 h-6 inline ml-2"/>
                        </div>
                    </div>
                    }

                    {/* total cost summary */}
                    <div className="border-b border-t py-2 flex justify-between items-center">
                        <div className="uppercase font-bold"><Trans id="total_cost">Total cost</Trans></div>
                        <Price
                            price={selectedAvailability === false ? "0.00" : availabilities[selectedAvailability].price}
                            currencySymbol={availabilities[selectedAvailability] ? availabilities[selectedAvailability].currencySymbol : ""}
                        />
                    </div>
                </div>

                <ModalFooter
                    onCancel={closeModal}
                    actionDisabled={selectedAvailability === false}
                    actionButtonText={preselectedDetails ? t({id: "amend_in_planner"}) : t({id: "add_to_planner"})}
                    onActionClick={addToCart}
                />
            </>
            }

            {overlapElements.length > 0 &&
                <OverlapWarning
                    overlapElements={overlapElements}
                    onCancel={hideOverlapWarning}
                    onConfirm={postToCart}
                    confirmDisabled={addingToCart}
                />
            }
        </Modal>

    );
};

export default RestaurantAvailabilityModal;

function AvailabilityOption(props) {
    const { t } = useLingui();
    const availability = props.availability;
    let classes = "relative p-4 border shadow flex md:flex-col border-primary cursor-pointer select-none";

    if(availability.selected) {
        classes += " bg-primary text-white";
    }
    else {
        classes += " text-grey-darker hover:bg-primary-lightest";
    }

    return (
        <div className="md:px-2 pb-4 md:w-1/4 text-center">
            <div className={classes}
            onClick={props.onClick}>
                {availability.selected &&
                <div className="diagonal-primary w-6 h-6 absolute top-0 right-0 text-white text-xs text-right">
                    &#x2714;
                </div>}

                <div className={`font-bold mr-2 ${availability.selected ? "" : "text-primary"}`}>{formatTimeHM(availability.fromTime)}</div>
                <div>
                    {t({id: "adult_label"})}{" "}
                    <Price unstyled price={availability.adultPrice} currencySymbol={props.currencySymbol} />
                </div>

                <div className="md:hidden mr-1">,</div>
                <div>
                    {t({id: "child_label"})}{" "}
                    {availability.maximumNumberOfChildren > 0 
                        ? <Price unstyled price={availability.childrenPrice} currencySymbol={props.currencySymbol} /> 
                        : t({id: "not_applicable_short"})}
                </div>

            </div>
        </div>
    );
}

const range = (start, stop) => Array.from({ length: stop - start + 1}, (_, i) => start + i );